import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { debounce } from 'lodash'; // Make sure lodash is installed
import { hasPermission } from '../../utils/permissions';
import NewLeadModal from '../leads/NewLeadModal';

// Move currencies outside of component to prevent recreation on each render
const currencies = {
  'Norway': 'NOK',
  'Sweden': 'SEK',
  'Germany': 'EUR',
  'Switzerland': 'CHF',
  'Luxembourg': 'EUR',
  'Austria': 'EUR',
  'United States': 'USD'
};

const countries = [
  'Norway',
  'Sweden',
  'Germany',
  'Switzerland',
  'Luxembourg',
  'Austria',
  'United States'
].sort();

const NewDealModal = ({ isOpen, onClose, onDealCreated, prefillData = {} }) => {
  // Get current date in YYYY-MM-DD format for default expected close date
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    lead_id: '',
    title: '',
    value: '',
    currency: '',
    stage_id: '',
    payment_method: 'Bank Transfer',
    country: '',
    course: '',
    course_id: '',
    service_type: '', // Track the service type
    notes: '',
    generate_invoice: false,
    student_name: '',
    student_age: '',
    deal_owner_id: '',
    order_number: ''
  });
  
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResultsVisible, setSearchResultsVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [stages, setStages] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [invoiceDueDays, setInvoiceDueDays] = useState(14);
  
  const searchRef = useRef(null);
  
  const paymentMethods = [
    'Bank Transfer',
    'Stripe',
    'SVEA'
  ];
  
  // Courses will be dynamically loaded from the database
  
  // Fetch leads and pipeline stages only when modal opens
  useEffect(() => {
    // Only run this effect when the modal opens
    if (!isOpen) return;

    // Using a ref to track mounted state
    let isMounted = true;
    
    // Clear previous state and set loading
    setLoading(true);
    setError(null);
    
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        // Get current user from localStorage only once
        let user = null;
        const userJson = localStorage.getItem('user');
        if (userJson) {
          user = JSON.parse(userJson);
        } else {
          // Fetch current user if not in localStorage
          const userRes = await axios.get('/api/auth/me', config);
          user = userRes.data;
          localStorage.setItem('user', JSON.stringify(user));
        }
        
        // Make all API calls at once
        console.log('Fetching leads and other data...');
        const [leadsRes, stagesRes, coursesRes, usersRes] = await Promise.all([
          axios.get('/api/leads', {
            ...config,
            params: {
              limit: 1000,  // Request more leads to ensure we get all
              page: 1
            }
          }),
          axios.get('/api/deals/stages/all', config),
          axios.get('/api/settings/courses/active', config),
          axios.get('/api/auth/users?has_deals_access=true', config) // Fetch only users with deals access
        ]);
        
        // Exit early if component unmounted during API calls
        if (!isMounted) return;
        
        // The leads API returns { leads: [...], pagination: {...} }
        const leadsList = leadsRes.data.leads || [];
        console.log(`Fetched ${leadsList.length} leads, pagination:`, leadsRes.data.pagination);
        const stagesList = stagesRes.data || [];
        const coursesList = coursesRes.data || [];
        
        // Users are already filtered by deals access from the API
        // No need for additional role-based filtering
        const eligibleUsers = usersRes.data;
        
        // Build the initial form data once
        const initialData = {
          lead_id: '',
          title: '',
          value: '',
          currency: '',
          stage_id: stagesList.length > 0 ? stagesList.find(stage => stage.name === 'Awaiting Payment')?.id || '1' : '1', // Default to 'Awaiting Payment'
          payment_method: 'Bank Transfer',
          country: '',
          course: '',
          course_id: '',
          service_type: '', // Initialize empty service type
          notes: '',
          generate_invoice: false,
          student_name: '',
          student_age: '',
          deal_owner_id: user ? user.id.toString() : '',
          order_number: ''
        };
        
        // Pre-fill form data if prefillData is provided
        if (prefillData) {
          // Only process if there's actual data to process
          if (Object.keys(prefillData).length > 0) {
            if (prefillData.lead_id) {
              // If a lead ID is provided, find the lead in the fetched leads
              const selectedLead = leadsList.find(lead => lead.id === prefillData.lead_id);
              
              if (selectedLead) {
                setSelectedLead(selectedLead);
                setSearchTerm(selectedLead.name);
                
                // Update initial data with lead information
                initialData.lead_id = selectedLead.id;
                initialData.country = selectedLead.country || '';
                initialData.currency = selectedLead.country ? currencies[selectedLead.country] || '' : '';
                initialData.student_name = selectedLead.name || '';
              }
            }
          }
        }
        
        // Exit early if component unmounted
        if (!isMounted) return;
        
        // Set all state variables in a single batch to avoid multiple renders
        setCurrentUser(user);
        setLeads(leadsList);
        setStages(stagesList);
        setCoursesList(coursesList);
        setSalesUsers(eligibleUsers);
        setFormData(initialData);
        setLoading(false);
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching data:', err);
          setError('Failed to load required data: ' + (err.response?.data?.message || err.message));
          setLoading(false);
        }
      }
    };
    
    // Execute the fetch once
    fetchData();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [isOpen, prefillData]); // Only depend on isOpen and prefillData
  
  // Handle escape key press
  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Add click outside and escape key handlers
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchResultsVisible(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [searchRef, handleEscapeKey]);
  
  // Create a debounced function to avoid too many API calls
  const debouncedSearch = useCallback(
    debounce(async (value) => {
      if (value.length < 2) {
        setFilteredLeads([]);
        setSearchLoading(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        // Query the API directly with the search term
        const response = await axios.get('/api/leads', {
          headers: {
            'x-auth-token': token
          },
          params: {
            search: value,
            limit: 10,
            page: 1
          }
        });
        
        console.log(`Search for "${value}" returned ${response.data.leads.length} leads`);
        setFilteredLeads(response.data.leads || []);
        setSearchLoading(false);
      } catch (error) {
        console.error('Error searching leads:', error);
        setFilteredLeads([]);
        setSearchLoading(false);
      }
    }, 300),
    [] // Dependencies empty because we don't want to recreate this function
  );
  
  // Handle lead search - queries the API directly for better performance with large databases
  const handleLeadSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSearchResultsVisible(true);
    setSearchLoading(true);
    
    // Call the debounced search function
    debouncedSearch(value);
  };
  
  // Handle lead selection
  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
    
    // Auto-fill country if the lead has it
    const newFormData = {
      ...formData,
      lead_id: lead.id
    };
    
    if (lead.country) {
      newFormData.country = lead.country;
      
      // Also update currency if there's a mapping for the country
      if (currencies[lead.country]) {
        newFormData.currency = currencies[lead.country];
      }
    }
    
    setFormData(newFormData);
    setSearchTerm(`${lead.name} - ${lead.email}`);
    setSearchResultsVisible(false);
  };

  // Handle when a new lead is created
  const handleNewLeadCreated = (newLead) => {
    // Add the new lead to our leads list
    setLeads([newLead, ...leads]);
    
    // Select the newly created lead
    handleLeadSelect(newLead);
    
    // Close the new lead modal
    setShowNewLeadModal(false);
  };
  
  // Update currency based on country selection
  // This effect is temporarily disabled to prevent infinite loops
  /*
  useEffect(() => {
    if (formData.country && currencies[formData.country]) {
      // Only update currency if it's different to avoid infinite loop
      if (formData.currency !== currencies[formData.country]) {
        setFormData(prev => ({
          ...prev,
          currency: currencies[formData.country]
        }));
      }
    }
  }, [formData.country, formData.currency, currencies]);
  */
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Create the updated form data
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    // If country is changed, update the currency as well
    if (name === 'country' && currencies[value] && currencies[value] !== formData.currency) {
      updatedFormData.currency = currencies[value];
    }
    
    setFormData(updatedFormData);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate that a lead is selected
      if (!selectedLead || !formData.lead_id) {
        setError('Please select a customer from the search results');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Construct the title from service/course and customer if not provided
      const dealTitle = formData.title || 
        `${formData.course} - ${selectedLead.name || 'Customer'}`;
      
      const dealData = {
        ...formData,
        title: dealTitle
      };
      
      const res = await axios.post('/api/deals', dealData, config);
      
      // If generate_invoice is checked, create an invoice from the deal
      if (formData.generate_invoice) {
        // Calculate due date from days
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + invoiceDueDays);
        
        const invoiceRes = await axios.post(`/api/deals/invoices/from-deal/${res.data.id}`, { 
          due_date: dueDate.toISOString() 
        }, config);
        
        // If invoice was created, open the PDF in a new tab using form submission
        if (invoiceRes.data && invoiceRes.data.id) {
          const token = localStorage.getItem('token');
          
          // Instead of client-side navigation, make a server request for the PDF
          // and download it directly as a blob
          axios({
            url: `/api/deals/invoices/${invoiceRes.data.id}/pdf`,
            method: 'GET',
            responseType: 'blob', // Important for handling binary data
            headers: {
              'x-auth-token': token
            }
          }).then((response) => {
            // Create a blob from the PDF stream
            const file = new Blob([response.data], { type: 'application/pdf' });
            
            // Create a link and trigger download
            const fileURL = URL.createObjectURL(file);
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', `invoice-${invoiceRes.data.invoice_number || invoiceRes.data.id}.pdf`);
            document.body.appendChild(fileLink);
            
            fileLink.click();
            
            // Clean up
            document.body.removeChild(fileLink);
            URL.revokeObjectURL(fileURL);
          }).catch(error => {
            console.error('Error downloading PDF:', error);
            alert('Failed to download PDF. Please try again.');
          });
        }
      }
      
      onDealCreated(res.data);
      onClose();
      
      // Reset form
      setFormData({
        lead_id: '',
        title: '',
        value: '',
        currency: '',
        stage_id: '',
        payment_method: 'Bank Transfer',
        country: '',
        course: '',
        course_id: '',
        service_type: '', // Reset service type
        notes: '',
        generate_invoice: false,
        student_name: '',
        student_age: '',
        deal_owner_id: currentUser ? currentUser.id.toString() : '',
        order_number: ''
      });
      setSearchTerm('');
      setSelectedLead(null);
    } catch (err) {
      console.error('Error creating deal:', err);
      setError('Failed to create deal: ' + (err.response?.data?.message || err.message));
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="p-3 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">Create New Deal</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {loading ? (
          <div className="p-3 text-center">Loading...</div>
        ) : error ? (
          <div className="p-3 text-center text-red-600">{error}</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-3">
            {/* Two-column layout for top section */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="col-span-2">
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Customer
                </label>
                <div className="flex items-center space-x-2">
                  <div className="relative flex-1" ref={searchRef}>
                    <input
                      type="text"
                      placeholder="Search by name or email"
                      value={searchTerm}
                      onChange={handleLeadSearch}
                      onFocus={() => setSearchResultsVisible(true)}
                      className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    />
                    {searchResultsVisible && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {searchLoading ? (
                          <div className="px-3 py-2 text-center text-gray-600">
                            Searching...
                          </div>
                        ) : filteredLeads.length > 0 ? (
                          filteredLeads.map(lead => (
                            <div
                              key={lead.id}
                              className="px-3 py-1.5 hover:bg-gray-100 cursor-pointer"
                              onClick={() => handleLeadSelect(lead)}
                            >
                              <div className="font-medium">
                                {lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim()}
                              </div>
                              <div className="text-sm text-gray-500">{lead.email}</div>
                              {lead.company && (
                                <div className="text-xs text-gray-500">{lead.company}</div>
                              )}
                            </div>
                          ))
                        ) : searchTerm.length > 1 && (
                          <div className="px-3 py-2 text-center text-gray-600">
                            No customers found. Try a different search term.
                          </div>
                        )}
                      </div>
                    )}
                    {selectedLead && (
                      <input 
                        type="hidden" 
                        name="lead_id" 
                        value={formData.lead_id}
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNewLeadModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-md transition-colors duration-200"
                    title="Create new customer"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Country
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                >
                  <option value="">Select Country</option>
                  {countries.map(country => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Service
                </label>
                <select
                  name="course_id"
                  value={formData.course_id}
                  onChange={(e) => {
                    // Get course_id, course name, and service_type when a service is selected
                    const selectedCourseId = e.target.value;
                    const selectedCourse = coursesList.find(c => c.id.toString() === selectedCourseId);
                    
                    setFormData({
                      ...formData, 
                      course_id: selectedCourseId,
                      course: selectedCourse ? selectedCourse.name : '', // Keep course name for backward compatibility
                      service_type: selectedCourse ? selectedCourse.service_type || 'education' : '' // Default to education for backward compatibility
                    });
                  }}
                  className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                >
                  <option value="">Select Service</option>
                  {coursesList.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                      {course.service_type === 'education' && ` - ${course.age_group} - Level ${course.level} - ${course.course_format}`}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Only show student fields if the service type is education */}
              {formData.service_type === 'education' && (
                <>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1">
                      Student Name
                    </label>
                    <input
                      type="text"
                      name="student_name"
                      value={formData.student_name}
                      onChange={handleChange}
                      placeholder="If different from customer"
                      className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1">
                      Student Age
                    </label>
                    <input
                      type="number"
                      name="student_age"
                      value={formData.student_age}
                      onChange={handleChange}
                      placeholder="Age"
                      min="0"
                      max="120"
                      className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                </>
              )}
            </div>
            
            {/* Price, Currency & Payment Method row */}
            <div className="flex mb-3 space-x-3">
              <div className="flex-1">
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="value"
                  value={formData.value}
                  onChange={handleChange}
                  placeholder={`Amount in ${formData.currency || 'local currency'}`}
                  className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                />
              </div>
              <div className="w-20">
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Currency
                </label>
                <input
                  type="text"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-100"
                  readOnly
                />
              </div>
              <div className="flex-1">
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Payment
                </label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                >
                  {paymentMethods.map(method => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Order Number, Stage and Close Date row */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Order Number
                </label>
                <input
                  type="number"
                  name="order_number"
                  value={formData.order_number}
                  onChange={handleChange}
                  placeholder="Auto-generated if empty"
                  className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank for auto-numbering
                </p>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Deal Stage
                </label>
                <select
                  name="stage_id"
                  value={formData.stage_id}
                  onChange={handleChange}
                  className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                >
                  <option value="">Select Stage</option>
                  {Array.isArray(stages) ? stages.map(stage => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  )) : <option value="">No stages available</option>}
                </select>
              </div>
              
              {/* Expected Close Date field removed */}
            </div>
            
            {/* Deal Owner - Visible for users with permission to assign deals */}
            {currentUser && (currentUser.role === 'superuser' || hasPermission('deals', 'assign')) && (
              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Deal Owner
                </label>
                <select
                  name="deal_owner_id"
                  value={formData.deal_owner_id}
                  onChange={handleChange}
                  className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="">Select Deal Owner</option>
                  {salesUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {currentUser.role === 'sales' ? 'You will be set as the deal owner automatically' : 'Select who will own this deal'}
                </p>
              </div>
            )}
            
            {/* Optional title and notes fields */}
            <div className="mb-3">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Custom Title (Optional)
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Auto-generate if blank"
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            
            <div className="mb-3">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                rows={2}
              />
            </div>
            
            <div className="mb-3">
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="generate_invoice"
                  name="generate_invoice"
                  checked={formData.generate_invoice}
                  onChange={(e) => setFormData({...formData, generate_invoice: e.target.checked})}
                  className="mr-2 h-4 w-4 text-blue-600"
                />
                <label htmlFor="generate_invoice" className="text-gray-700 text-sm">
                  Generate PDF Invoice Automatically
                </label>
              </div>
              {formData.generate_invoice && (
                <div className="ml-6 flex items-center">
                  <label htmlFor="invoiceDueDays" className="text-gray-700 mr-2 text-sm">
                    Due in:
                  </label>
                  <input
                    type="number"
                    id="invoiceDueDays"
                    min="1"
                    max="365"
                    value={invoiceDueDays}
                    onChange={(e) => setInvoiceDueDays(parseInt(e.target.value) || 14)}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                  />
                  <span className="text-gray-700 ml-1 text-sm">days</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-500 text-white px-3 py-1.5 rounded-md hover:bg-gray-600 mr-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 text-sm"
              >
                Create Deal
              </button>
            </div>
          </form>
        )}
      </div>
      
      {/* New Lead Modal */}
      <NewLeadModal
        isOpen={showNewLeadModal}
        onClose={() => setShowNewLeadModal(false)}
        onLeadCreated={handleNewLeadCreated}
      />
    </div>
  );
};

export default NewDealModal;
